// Centralized Business Configuration
// All business data should be sourced from here for consistency
// This structure supports future dynamic Google review integration

export const businessConfig = {
  // Company Information
  company: {
    name: 'Thrifty Move',
    legalName: 'DV Amur Services Pty Ltd',
    tradingAs: 'Thrifty Move',
    abn: '47701703344',
    tagline: 'Professional Melbourne Removalists',
    description: 'Family-owned Melbourne removal company focused on clear pricing, reliable communication and careful handling.',
    foundedYear: 2024,
    foundedDescription: 'late 2024'
  },

  // Contact Details
  contact: {
    phone: '1800 233 023',
    phoneFormatted: '1800 233 023',
    phoneLink: 'tel:1800233023',
    email: 'info@thrifty-move.com.au',
    whatsapp: '+61 494 078 888',
    whatsappUrl: 'https://wa.me/message/CTSACM3X6CMVN1',
    location: 'Oakleigh South, VIC 3167',
    serviceArea: 'Servicing Melbourne & surrounding suburbs'
  },

  // Google Reviews - Centralized for future dynamic integration
  reviews: {
    platform: 'Google',
    rating: 5.0,
    count: 41,
    displayText: '5.0 from 41+ Google Reviews',
    lastUpdated: '2025-12-01' // Update when syncing
  },

  // Insurance Coverage
  insurance: {
    publicLiability: '$10 million',
    goodsInTransit: '$100,000',
    description: 'Fully insured with $10M Public Liability and $100K Goods in Transit coverage'
  },

  // Pricing Tiers (display only - backend calculates actual quotes)
  // Updated December 2025 - New pricing structure
  pricing: {
    tiers: [
      {
        id: 'small',
        label: '4-6 Tonne Truck',
        volumeRange: '0–22 m³',
        hourlyRate: 130,
        description: 'Apartments, studios, small moves'
      },
      {
        id: 'medium',
        label: '8-10 Tonne Truck',
        volumeRange: '22–32 m³',
        hourlyRate: 140,
        description: '2-3 bedroom homes, medium moves'
      },
      {
        id: 'large',
        label: '12+ Tonne Truck',
        volumeRange: '32–52 m³',
        hourlyRate: 187,
        hourlyRateDisplay: 'From $187',
        description: 'Larger homes, office relocations'
      }
    ],
    includes: ['2 professional removalists', 'Suitable moving truck', '2-hour minimum booking', 'GST included'],
    modifiers: {
      sundaySurcharge: '+$10/hour on Sundays'
    },
    calloutNote: 'Additional travel/call-out fee applies based on your location. Calculated automatically during the quote process.'
  },

  // Services Offered
  services: [
    {
      id: 'house-removals',
      title: 'House Removals',
      shortDescription: 'Complete home moving services across Melbourne',
      icon: 'Home',
      available: true
    },
    {
      id: 'apartment-removals',
      title: 'Apartment Removals',
      shortDescription: 'Specialist apartment and unit moves',
      icon: 'Building2',
      available: true
    },
    {
      id: 'office-removals',
      title: 'Office Removals',
      shortDescription: 'Professional business and office relocations',
      icon: 'Briefcase',
      available: true
    },
    {
      id: 'furniture-removals',
      title: 'Furniture Removals',
      shortDescription: 'Individual furniture items and deliveries',
      icon: 'Sofa',
      available: true
    },
    {
      id: 'packing',
      title: 'Packing Services',
      shortDescription: 'Professional packing assistance',
      icon: 'Package',
      available: true,
      note: 'Availability depends on move requirements'
    },
    {
      id: 'unpacking',
      title: 'Unpacking Services',
      shortDescription: 'Help settling into your new space',
      icon: 'PackageOpen',
      available: true,
      note: 'Availability depends on move requirements'
    }
  ],

  // Service Areas
  serviceAreas: {
    primary: 'Melbourne',
    description: 'Servicing Melbourne and surrounding suburbs, with strong coverage across Melbourne\'s South-East and Eastern suburbs.',
    regions: [
      {
        name: 'South-East Melbourne',
        highlight: true,
        suburbs: ['Oakleigh', 'Clayton', 'Bentleigh', 'Brighton', 'Caulfield', 'Carnegie', 'Glen Waverley', 'Dandenong', 'Springvale', 'Noble Park', 'Cheltenham', 'Moorabbin']
      },
      {
        name: 'Eastern Melbourne',
        highlight: true,
        suburbs: ['Hawthorn', 'Kew', 'Camberwell', 'Box Hill', 'Doncaster', 'Ringwood', 'Blackburn', 'Burwood', 'Mont Albert', 'Balwyn']
      },
      {
        name: 'Inner Melbourne',
        highlight: false,
        suburbs: ['Melbourne CBD', 'Southbank', 'South Yarra', 'Richmond', 'Fitzroy', 'Collingwood', 'Carlton', 'Brunswick', 'St Kilda', 'Prahran', 'Port Melbourne']
      },
      {
        name: 'Northern Melbourne',
        highlight: false,
        suburbs: ['Preston', 'Thornbury', 'Northcote', 'Coburg', 'Reservoir', 'Heidelberg', 'Bundoora', 'Mill Park', 'Epping']
      },
      {
        name: 'Western Melbourne',
        highlight: false,
        suburbs: ['Footscray', 'Sunshine', 'Werribee', 'Point Cook', 'Williamstown', 'Altona', 'Yarraville', 'Maribyrnong']
      }
    ]
  },

  // Social Media (only confirmed URLs)
  social: {
    facebook: 'https://facebook.com/thriftymove',
    instagram: 'https://instagram.com/thriftymove'
  },

  // Brand Assets
  assets: {
    logo: 'https://customer-assets.emergentagent.com/job_thrifty-moving/artifacts/kpk9qkbr_Untitled%20design.png',
    truckPhoto: 'https://customer-assets.emergentagent.com/job_quickquote-18/artifacts/30lhribn_5%20thrifty.jpg',
    equipmentPhoto: 'https://customer-assets.emergentagent.com/job_quickquote-18/artifacts/746xsx1w_fotka%202.jpg'
  },

  // Trust Points (verified facts only)
  trustPoints: [
    { text: 'Family Owned', icon: 'Users' },
    { text: 'Fully Insured', icon: 'Shield' },
    { text: 'Melbourne Based', icon: 'MapPin' },
    { text: 'Clear Pricing', icon: 'DollarSign' }
  ],

  // Why Choose Us (verified differentiators)
  differentiators: [
    {
      title: 'Clear Communication',
      description: 'From quote to moving day, you\'ll always know exactly what\'s happening with your move.',
      icon: 'MessageCircle'
    },
    {
      title: 'Transparent Pricing',
      description: 'Volume-based pricing tiers. No hidden fees. Your quote shows exactly what you\'ll pay.',
      icon: 'Calculator'
    },
    {
      title: 'Right Capacity',
      description: 'Our quote system matches your move with the appropriate truck capacity and crew.',
      icon: 'Truck'
    },
    {
      title: 'Careful Handling',
      description: 'Professional equipment, moving blankets, and trained removalists protect your belongings.',
      icon: 'ShieldCheck'
    },
    {
      title: 'Fully Insured',
      description: '$10M public liability and $100K goods in transit coverage for your peace of mind.',
      icon: 'Shield'
    },
    {
      title: 'Easy Online Quotes',
      description: 'Build your inventory online, see your estimated volume, and get accurate pricing.',
      icon: 'Globe'
    }
  ],

  // How It Works Steps
  howItWorks: [
    {
      step: 1,
      title: 'Build Your Quote',
      description: 'Use our online system to list your items and get an estimated volume and price.'
    },
    {
      step: 2,
      title: 'We Review & Confirm',
      description: 'Our team reviews your requirements and confirms availability and final details.'
    },
    {
      step: 3,
      title: 'Moving Day',
      description: 'Professional removalists arrive on time and handle your move with care.'
    }
  ]
};

// Helper function for tracking phone clicks (GA4 events, not conversions)
// Lead conversions are tracked on ThankYouPage with the full conversion label
// Phone call conversions are handled automatically by gtag phone_conversion_number
export const trackPhoneClick = (eventLabel) => {
  if (window.gtag) {
    window.gtag('event', 'phone_click', {
      'event_category': 'Phone',
      'event_label': eventLabel
    });
  }
};

// Google Ads Conversion Tracking Configuration
// Conversion ID: AW-17855604804
// Lead Conversion Label: nwAYCLPT2t8bEMTQm8JC (fires on ThankYouPage)
// Phone Conversion Label: ZC0CKqVkZwdEMTQm8JC (automatic via gtag config)
export const GOOGLE_ADS_CONVERSION = {
  id: 'AW-17855604804',
  leadLabel: 'nwAYCLPT2t8bEMTQm8JC',
  phoneLabel: 'ZC0CKqVkZwdEMTQm8JC'
};

export default businessConfig;
